import Dialog from "../Dialog";
import { discardChanges, useConfirm } from "../ConfirmProvider";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import { Loader2, Plus } from "lucide-react";
import React, { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { TASK_STATUSES } from "./taskConstants";

export const TaskFormModal = ({ clients, defaultRate, onCancel, onSubmit }) => {
  const formId = useId();
  const lock = useRef(false);
  const [submitError, setSubmitError] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting, dirtyFields },
  } = useForm({
    defaultValues: {
      client_id: clients[0]?.id || "",
      titre: "",
      commentaire: "",
      notes_internes: "",
      statut: "a_faire",
      heures_estimees: "",
      taux_horaire: defaultRate,
      date_tache: new Date().toISOString().slice(0, 10),
      facturable: true,
    },
  });

  useEffect(() => {
    reset({
      client_id: clients[0]?.id || "",
      titre: "",
      commentaire: "",
      notes_internes: "",
      statut: "a_faire",
      heures_estimees: "",
      taux_horaire: defaultRate,
      date_tache: new Date().toISOString().slice(0, 10),
      facturable: true,
    }, { keepDirtyValues: true });
  }, [clients, defaultRate, reset]);

  const previousClient = useRef(null);
  const clientId = watch("client_id");
  const selectedClient = clients.find((client) => client.id === clientId);
  useEffect(() => {
    if ((previousClient.current !== null && previousClient.current !== clientId) || !dirtyFields.taux_horaire) setValue("taux_horaire", selectedClient?.taux_horaire ?? defaultRate);
    previousClient.current = clientId;
  }, [selectedClient, clientId, defaultRate, dirtyFields.taux_horaire, setValue]);

  const confirm = useConfirm();
  useUnsavedChanges(isDirty);
  const close = async () => { if (!isSubmitting && (!isDirty || await confirm(discardChanges))) onCancel(); };
  const submit = async (data) => {
    if (lock.current) return;
    lock.current = true; setSubmitError("");
    try { await onSubmit({
      ...data, heures_estimees: data.heures_estimees === "" ? null : Number(data.heures_estimees),
      taux_horaire: data.taux_horaire === "" ? null : Number(data.taux_horaire), facturable: Boolean(data.facturable),
    }); } catch (failure) { setSubmitError(failure?.response?.data?.detail || "Impossible de créer la tâche. Vos modifications sont conservées."); }
    finally { lock.current = false; }
  };

  return (
    <Dialog footer={<>
            <button type="button" onClick={close} disabled={isSubmitting} className="btn-secondary">
              Annuler
            </button>
            <button form={formId} type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? "Création..." : "Créer la tâche"}
            </button>
           </>} title="Nouvelle tâche" onClose={close} busy={isSubmitting} size="max-w-3xl">
        <form id={formId} onSubmit={handleSubmit(submit)} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Client *
              </label>
              <select
                className="input-field"
                {...register("client_id", {
                  required: "Le client est obligatoire",
                })}
              >
                <option value="">Sélectionner un client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom} {client.societe ? `- ${client.societe}` : ""}
                  </option>
                ))}
              </select>
              {errors.client_id && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.client_id.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select className="input-field" {...register("statut")}>
                {TASK_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Titre *
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="Ex: Mise à jour CMS client X"
              {...register("titre", { required: "Le titre est obligatoire" })}
            />
            {errors.titre && (
              <p className="mt-1 text-sm text-red-600">{errors.titre.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Commentaire
              </label>
              <textarea
                rows={4}
                className="input-field"
                {...register("commentaire")}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Notes internes
              </label>
              <textarea
                rows={4}
                className="input-field"
                {...register("notes_internes")}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Heures estimées
              </label>
              <input
                type="number"
                min="0"
                step="0.25"
                className="input-field"
                {...register("heures_estimees")}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Tarif horaire ({selectedClient?.devise || "devise du client"} / heure)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                {...register("taux_horaire")}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Date
              </label>
              <input type="date" className="input-field" {...register("date_tache")} />
            </div>
          </div>

          <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3">
            <input type="checkbox" {...register("facturable")} />
            <span className="text-sm text-gray-700">Tâche facturable</span>
          </label>


          {submitError && <p role="alert" className="text-sm text-red-700">{submitError}</p>}
        </form>
    </Dialog>
  );
};

export const TaskQuickCreateRow = ({
  status,
  active,
  quickCreate,
  clients,
  filteredClientId,
  creating,
  onActivate,
  onCancel,
  onChange,
  onSubmit,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (active) {
      inputRef.current?.focus();
    }
  }, [active]);

  if (!active) {
    return (
      <button
        type="button"
        onClick={() => onActivate(status)}
        className="flex w-full items-center gap-2 rounded-b-2xl border-t border-slate-200 px-4 py-3 text-sm font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
      >
        <Plus className="h-4 w-4" />
        Ajouter tâche
      </button>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="border-t border-slate-200 bg-slate-50/80 px-4 py-4"
    >
      <div className="grid gap-3 lg:grid-cols-[minmax(18rem,2.4fr)_12rem_auto]">
        <input
          ref={inputRef}
          type="text"
          value={quickCreate.titre}
          onChange={(event) => onChange("titre", event.target.value)}
          className="input-field rounded-xl bg-white"
          placeholder="Nom de la tâche"
        />

        {filteredClientId ? (
          <div className="flex items-center rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600">
            Client filtré conservé
          </div>
        ) : (
          <select
            value={quickCreate.client_id}
            onChange={(event) => onChange("client_id", event.target.value)}
            className="input-field rounded-xl bg-white"
          >
            <option value="">Choisir un client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.nom}
              </option>
            ))}
          </select>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Annuler
          </button>
          <button type="submit" disabled={creating} className="btn-primary">
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            <span>Créer</span>
          </button>
        </div>
      </div>
    </form>
  );
};
